// @ts-check
'use strict';

/**
 * Report summaries — whole site, sector lead-in, per area — written from
 * structured facts only. No AI: wording comes from authored variant pools,
 * picked by a stable seed (same report → same wording; different sites →
 * different wording). A stored override is always used as written.
 * Ported from Site2Scope's src/summaryLibrary.js.
 */

const { SECTOR_BY_KEY } = require('./vocabulary');
const { BAND_LABEL } = require('./scales');

/** @typedef {import('./scales').Band} Band */
/** @typedef {Record<string, string | number | null>} Facts */
/** @typedef {{requires: string[], text: string}} Variant */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];


/** 'YYYY-MM-DD' → 'Month YYYY'; '' if unparseable. @param {unknown} iso */
function formatMonthYear(iso) {
  const m = typeof iso === 'string' && iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const month = parseInt(m[2], 10) - 1;
  return month >= 0 && month <= 11 ? `${MONTHS[month]} ${m[1]}` : '';
}

/** [a] → 'a'; [a,b] → 'a and b'; [a,b,c] → 'a, b and c'. @param {string[]} items */
function joinList(items) {
  const xs = items.filter(Boolean);
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/** Present = not null/undefined/''. Zero counts as present. @param {unknown} v */
const factPresent = (v) => v !== null && v !== undefined && v !== '';

/** djb2 string hash → non-negative integer. @param {unknown} str */
function hashSeed(str) {
  let h = 5381;
  const s = String(str == null ? '' : str);
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Fill {token} slots from facts; absent facts leave the token. @param {string} template @param {Facts} facts */
function fillTemplate(template, facts) {
  return String(template).replace(/\{(\w+)\}/g, (m, key) => (factPresent(facts[key]) ? String(facts[key]) : m));
}

/**
 * The variants whose required facts are all present, then one picked by seed.
 *
 * @param {Variant[]} pool
 * @param {Facts} facts
 * @param {string} seed
 * @returns {Variant | null}
 */
function selectVariant(pool, facts, seed) {
  const eligible = (pool || []).filter((v) => (v.requires || []).every((k) => factPresent(facts[k])));
  return eligible.length ? eligible[hashSeed(seed) % eligible.length] : null;
}

/** @type {Record<Band, Variant[]>} */
const OVERALL_VARIANTS = {
  poor: [
    { requires: ['siteName', 'areaTotal'], text: 'This assessment of {siteName} found {areaPoor} of {areaTotal} areas in poor condition requiring immediate attention. The overall site condition is rated Poor.' },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'Higgins assessed {siteName} in {monthYear}. Of {areaTotal} areas inspected, {areaPoor} require immediate attention and {areaFair} warrant planned maintenance, placing the site in overall Poor condition.' },
    { requires: ['siteName', 'location', 'areaTotal'], text: 'At {siteName} in {location}, {areaPoor} of {areaTotal} areas are in poor condition and need prompt remediation. Overall the site is rated Poor.' },
  ],
  fair: [
    { requires: ['siteName', 'areaTotal'], text: 'Of {areaTotal} areas assessed at {siteName}, {areaFair} warrant planned maintenance and {areaGood} remain serviceable, giving an overall condition of Fair.' },
    { requires: ['siteName', 'location', 'client', 'monthYear', 'areaTotal'], text: "Higgins assessed {siteName} at {location} for {client} in {monthYear}. Of the site's {areaTotal} areas, {areaPoor} require immediate attention, {areaFair} warrant planned maintenance, and {areaGood} remain serviceable — an overall condition of Fair." },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'The {monthYear} assessment of {siteName} rated the site Fair overall. Across {areaTotal} areas, {areaFair} show early deterioration warranting planned maintenance while {areaGood} remain serviceable.' },
  ],
  good: [
    { requires: ['siteName', 'areaTotal'], text: '{siteName} presents in good condition: {areaGood} of {areaTotal} areas are serviceable and the overall site condition is rated Good.' },
    { requires: ['siteName', 'monthYear', 'areaTotal'], text: 'The {monthYear} assessment of {siteName} found the site in good overall condition, with {areaGood} of {areaTotal} areas serviceable and no widespread deterioration.' },
    { requires: ['siteName', 'location', 'areaTotal'], text: 'At {siteName} in {location}, the coatings are generally sound — {areaGood} of {areaTotal} areas are serviceable and the site is rated Good overall.' },
  ],
};

/** @type {Variant[]} */
const RISK_SENTENCES = [
  { requires: ['riskList'], text: 'The principal risks identified are {riskList}.' },
  { requires: ['dominantRisk'], text: 'Coating condition across the site is dominated by {dominantRisk}.' },
];

/** @type {Variant[]} */
const AGE_SENTENCES = [
  { requires: ['buildingAge'], text: 'The buildings date from the {buildingAge}, and their condition reflects age and exposure.' },
  { requires: ['buildingAge', 'location'], text: 'Typical of {buildingAge} construction in {location}, weathering is most evident on exposed elements.' },
];

// Neutral across sectors (Site2Scope's "resident amenity" fitted aged care only).
/** @type {Variant[]} */
const SECTOR_VARIANTS = [
  { requires: ['sectorLabel'], text: 'As a {sectorLabel} site, presentation and continuity of service are priorities; the findings below are grouped to support planned, low-disruption maintenance.' },
  { requires: ['sectorLabel', 'client'], text: 'For {client}, this {sectorLabel} site is assessed with continuity of service in mind — the register below highlights where intervention protects both compliance and appearance.' },
];

/** @type {Record<Band, Variant[]>} */
const AREA_VARIANTS = {
  poor: [
    { requires: ['areaName'], text: '{areaName} is in poor condition and is a priority for remediation.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} requires immediate attention; the recommended delivery is {areaAction}.' },
  ],
  fair: [
    { requires: ['areaName'], text: '{areaName} shows early deterioration and warrants planned maintenance.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} is serviceable but declining; {areaAction} is recommended to protect coating life.' },
  ],
  good: [
    { requires: ['areaName'], text: '{areaName} is in good condition and remains serviceable.' },
    { requires: ['areaName', 'areaAction'], text: '{areaName} presents well; {areaAction} only is recommended.' },
  ],
};

/**
 * The flat fact set the variants draw on.
 *
 * @param {import('./view-model').VisitMeta} meta
 * @param {import('./view-model').ReportViewModel} vm
 * @returns {Facts}
 */
function buildFacts(meta, vm) {
  const m = meta || {};
  const as = vm.areaStats;
  const risks = vm.keyRisks.map((r) => (r.label || '').toLowerCase()).filter(Boolean);
  const repaintCount = vm.outlookRows.filter((r) => r.deliveryMode === 'repaint_year').length;
  const sector = m.sector || null;
  return {
    client: m.clientName || null,
    siteName: m.siteName || null,
    location: m.location || null,
    sector,
    sectorLabel: sector && SECTOR_BY_KEY[sector] ? SECTOR_BY_KEY[sector].label : null,
    buildingAge: m.buildingAge || null,
    notableItems: m.notableItems || null,
    monthYear: formatMonthYear(m.inspectionDate) || null,
    overallBand: vm.overallBand,
    overallLabel: vm.overallBand ? BAND_LABEL[vm.overallBand] : null,
    areaPoor: as.poor, areaFair: as.fair, areaGood: as.good, areaTotal: as.total,
    itemPoor: vm.stats.poor, itemFair: vm.stats.fair, itemGood: vm.stats.good, itemTotal: vm.stats.total,
    dominantRisk: risks[0] || null,
    riskList: risks.length ? joinList(risks.slice(0, 3)) : null,
    repaintCount,
  };
}

/**
 * 3–5 sentences: the band's opener, then risks, building age, repaint count and
 * the assessor's notable items where present.
 *
 * @param {Facts} facts
 * @param {string | null} [override]
 */
function generateOverallSummary(facts, override) {
  if (factPresent(override)) return /** @type {string} */ (override);
  const band = /** @type {Band | null} */ (facts.overallBand);
  if (!band) return '';
  const seed = `${facts.siteName}|${band}|${facts.monthYear}`;
  const base = selectVariant(OVERALL_VARIANTS[band], facts, `${seed}|overall`);
  if (!base) return '';
  const parts = [fillTemplate(base.text, facts)];
  const risk = factPresent(facts.riskList) && selectVariant(RISK_SENTENCES, facts, `${seed}|risk`);
  if (risk) parts.push(fillTemplate(risk.text, facts));
  const age = factPresent(facts.buildingAge) && selectVariant(AGE_SENTENCES, facts, `${seed}|age`);
  if (age) parts.push(fillTemplate(age.text, facts));
  if (Number(facts.repaintCount) > 0) {
    parts.push(fillTemplate('Higgins recommends prioritising the areas in poorest condition, with {repaintCount} flagged for full repaint.', facts));
  }
  if (factPresent(facts.notableItems)) {
    const ni = String(facts.notableItems).trim();
    parts.push((ni.charAt(0).toUpperCase() + ni.slice(1)).replace(/\.?$/, '.'));
  }
  return parts.join(' ');
}

/** @param {Facts} facts @param {string | null} [override] */
function generateSectorSummary(facts, override) {
  if (factPresent(override)) return /** @type {string} */ (override);
  if (!factPresent(facts.sectorLabel)) return '';
  const v = selectVariant(SECTOR_VARIANTS, facts, `${facts.siteName}|${facts.sector}|sector`);
  return v ? fillTemplate(v.text, facts) : '';
}

/** @param {Facts} areaFacts @param {Band | null} band @param {string} seed @param {string | null} [override] */
function generateAreaSummary(areaFacts, band, seed, override) {
  if (factPresent(override)) return /** @type {string} */ (override);
  if (!band) return '';
  const v = selectVariant(AREA_VARIANTS[band], areaFacts, seed);
  return v ? fillTemplate(v.text, areaFacts) : '';
}

/**
 * All three tiers for a report, honouring the visit's stored overrides.
 *
 * @param {import('./view-model').VisitMeta} meta
 * @param {import('./view-model').ReportViewModel} vm
 */
function buildReportSummaries(meta, vm) {
  const m = meta || {};
  const facts = buildFacts(m, vm);
  /** @type {Record<string, string>} */
  const sectors = {};
  if (facts.sector) {
    const key = String(facts.sector);
    sectors[key] = generateSectorSummary(facts, (m.sectorSummaryOverride || {})[key]);
  }
  /** @type {Record<string, string>} */
  const areas = {};
  for (const group of vm.register) {
    const areaFacts = { ...facts, areaName: group.area, areaAction: (group.rows.find((r) => r.action) || {}).action || null };
    const seed = `${facts.siteName}|${group.area}|${group.band}|area`;
    areas[group.area] = generateAreaSummary(areaFacts, group.band, seed, (m.areaSummaryOverride || {})[group.area]);
  }
  return { overall: generateOverallSummary(facts, m.overallSummaryOverride), sectors, areas };
}

module.exports = {
  hashSeed,
  fillTemplate,
  selectVariant,
  buildFacts,
  generateOverallSummary,
  generateSectorSummary,
  generateAreaSummary,
  buildReportSummaries,
};
